import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faPen, faPlus, faPowerOff, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../FlashMessage';
import {
    activateBenchmark,
    deactivateBenchmark,
    fetchBenchmarks,
} from '../../services/paperPricingService';
import {
    BATE_LABELS,
    PRICE_STATUS_LABELS,
    formatCount,
    formatDay,
    formatPrice,
    refusalText,
    requireList,
    unitLabel,
} from './paperPricingFormat';
import { ActiveStatus, Dash, LoadProblem, textOrDash } from './PaperPricingParts';
import useMasterList from './useMasterList';
import BenchmarkFormModal from './BenchmarkFormModal';

const COLUMNS = 6;

// The detail says which instruments use a benchmark. Without that list it would
// claim "none", which is exactly when deactivating matters most.
const checkBenchmark = (benchmark) => requireList(benchmark, 'instruments');

const BenchmarkDetail = ({ benchmark, acting, actionError, onToggle, onEdit, onClose }) => {
    const latest = benchmark.latest_price;
    return (
        <aside className="pp-detail" aria-label={`${benchmark.code} details`}>
            <div className="pp-detail-head">
                <div className="pp-detail-titles">
                    <div className="pp-detail-eyebrow">Benchmark index</div>
                    <h3 className="pp-detail-title">{benchmark.name}</h3>
                    <div className="pp-detail-sub">
                        <span className="pp-code">{benchmark.code}</span>
                        <ActiveStatus active={benchmark.is_active} />
                    </div>
                </div>
                <button type="button" className="pp-icon-btn" aria-label="Close details" onClick={onClose}>
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>

            <dl className="pp-detail-list">
                <dt>Official description</dt>
                <dd>{textOrDash(benchmark.description)}</dd>
                <dt>Provider</dt>
                <dd>{textOrDash(benchmark.provider_label)}</dd>
                <dt>Default price point</dt>
                <dd>{benchmark.default_bate_label} ({benchmark.default_bate})</dd>
                <dt>Product</dt>
                <dd>{textOrDash(benchmark.product)}</dd>
                <dt>Location</dt>
                <dd>{textOrDash(benchmark.location)}</dd>
                <dt>Region</dt>
                <dd>{textOrDash(benchmark.region_label)}</dd>
                <dt>Basis</dt>
                <dd>{textOrDash(benchmark.basis)}</dd>
                <dt>Quoted unit</dt>
                <dd>{unitLabel(benchmark.currency, benchmark.unit)}</dd>
                <dt>Valid from</dt>
                <dd>{benchmark.valid_from ? formatDay(benchmark.valid_from) : <Dash />}</dd>
                <dt>Price source</dt>
                <dd>{textOrDash(benchmark.price_source)}</dd>
                <dt>Latest price</dt>
                <dd>
                    {latest ? (
                        <>
                            <span className="pp-num-inline">{formatPrice(latest.value)}</span>
                            {' '}{BATE_LABELS[latest.bate] || latest.bate} on {formatDay(latest.assessed_on)}
                            {PRICE_STATUS_LABELS[latest.status] ? ` · ${PRICE_STATUS_LABELS[latest.status]}` : ''}
                        </>
                    ) : 'No price yet'}
                </dd>
            </dl>

            <div className="pp-detail-section">
                <div className="pp-detail-section-title">Used by paper instruments</div>
                {benchmark.instruments.length ? (
                    <ul className="pp-detail-items">
                        {benchmark.instruments.map((instrument) => (
                            <li key={instrument.id}>
                                {instrument.name}
                                {instrument.is_active ? null : <span className="pp-muted"> (inactive)</span>}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="pp-muted pp-detail-none">No paper instrument uses this benchmark yet.</p>
                )}
            </div>

            {actionError ? <div className="pp-alert pp-alert--error" role="alert">{actionError}</div> : null}

            <div className="pp-detail-actions">
                <button type="button" className="pp-btn pp-btn--secondary" onClick={onToggle} disabled={acting}>
                    <FontAwesomeIcon icon={faPowerOff} /> {benchmark.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button type="button" className="pp-btn pp-btn--primary" onClick={onEdit} disabled={acting}>
                    <FontAwesomeIcon icon={faPen} /> Edit
                </button>
            </div>
        </aside>
    );
};

/**
 * Benchmark indexes (ledger row 132, AC1, AC2, AC4, AC5): every price the company
 * may price a deal against. A benchmark is never deleted; it is deactivated, and
 * the server refuses that while an active paper instrument still settles off it.
 */
const BenchmarkIndexes = () => {
    const { showSuccess } = useFlashMessage();
    const { search, setSearch, list, loading, error, reload } = useMasterList(
        fetchBenchmarks,
        'The benchmark list could not be loaded. Try again.',
        checkBenchmark,
    );
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(null);
    const [acting, setActing] = useState(false);
    const [actionError, setActionError] = useState('');

    const results = list ? list.results : [];
    const selected = results.find((benchmark) => benchmark.id === selectedId) || null;

    const select = (id) => {
        setActionError('');
        setSelectedId((current) => (current === id ? null : id));
    };

    const toggleActive = async (benchmark) => {
        setActing(true);
        setActionError('');
        try {
            const updated = benchmark.is_active
                ? await deactivateBenchmark(benchmark.id)
                : await activateBenchmark(benchmark.id);
            showSuccess(`${updated.code} is now ${updated.is_active ? 'active' : 'inactive'}.`);
            reload();
        } catch (err) {
            setActionError(refusalText(
                err,
                `${benchmark.code} could not be ${benchmark.is_active ? 'deactivated' : 'activated'}. Try again.`,
            ));
        } finally {
            setActing(false);
        }
    };

    const handleSaved = (saved, wasEditing) => {
        setForm(null);
        showSuccess(wasEditing ? `Saved ${saved.code}.` : `Added ${saved.code} to the benchmark list.`);
        setActionError('');
        setSelectedId(saved.id);
        reload();
    };

    const emptyText = search.trim()
        ? 'No benchmark matches your search.'
        : 'No benchmarks yet. Add the first one with Add benchmark.';

    return (
        <>
            <div className="pp-toolbar">
                <div className="pp-search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="pp-search-icon" />
                    <input
                        type="search"
                        className="pp-input pp-search-input"
                        placeholder="Name, Platts code, product or location"
                        aria-label="Search benchmarks"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
                <span className="pp-count">
                    {list ? `${formatCount(list.counts.active)} active · ${formatCount(list.counts.inactive)} inactive` : ''}
                </span>
                <button
                    type="button"
                    className="pp-btn pp-btn--primary pp-toolbar-action"
                    onClick={() => setForm({ mode: 'add' })}
                >
                    <FontAwesomeIcon icon={faPlus} /> Add benchmark
                </button>
            </div>

            {error ? (
                <LoadProblem message={error} onRetry={reload} />
            ) : (
                <div className={`pp-master ${selected ? 'pp-master--with-detail' : ''}`}>
                    <div className={`pp-table-scroll ${loading && list ? 'pp-dim' : ''}`}>
                        <table className="pp-table pp-table--master">
                            <thead>
                                <tr>
                                    <th scope="col">Display name</th>
                                    <th scope="col">Code</th>
                                    <th scope="col">Product</th>
                                    <th scope="col">Location</th>
                                    <th scope="col">Unit</th>
                                    <th scope="col">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && !list ? (
                                    <tr><td colSpan={COLUMNS} className="pp-state">Loading benchmarks…</td></tr>
                                ) : results.length === 0 ? (
                                    <tr><td colSpan={COLUMNS} className="pp-state">{emptyText}</td></tr>
                                ) : results.map((benchmark) => (
                                    <tr
                                        key={benchmark.id}
                                        className={[
                                            'pp-row--clickable',
                                            benchmark.id === selectedId ? 'pp-row--selected' : '',
                                            benchmark.is_active ? '' : 'pp-row--inactive',
                                        ].join(' ')}
                                        onClick={() => select(benchmark.id)}
                                    >
                                        <td className="pp-cell-strong">
                                            <button
                                                type="button"
                                                className="pp-link-btn"
                                                aria-expanded={benchmark.id === selectedId}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    select(benchmark.id);
                                                }}
                                            >
                                                {benchmark.name}
                                            </button>
                                        </td>
                                        <td><span className="pp-code">{benchmark.code}</span></td>
                                        <td>{textOrDash(benchmark.product)}</td>
                                        <td>{textOrDash(benchmark.location)}</td>
                                        <td className="pp-nowrap">{unitLabel(benchmark.currency, benchmark.unit)}</td>
                                        <td><ActiveStatus active={benchmark.is_active} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {selected ? (
                        <BenchmarkDetail
                            benchmark={selected}
                            acting={acting}
                            actionError={actionError}
                            onToggle={() => toggleActive(selected)}
                            onEdit={() => setForm({ mode: 'edit', benchmark: selected })}
                            onClose={() => select(selected.id)}
                        />
                    ) : null}
                </div>
            )}

            {form ? (
                <BenchmarkFormModal
                    benchmark={form.mode === 'edit' ? form.benchmark : null}
                    onClose={() => setForm(null)}
                    onSaved={handleSaved}
                />
            ) : null}
        </>
    );
};

export default BenchmarkIndexes;
